/**
 * useSalesChat — Client-side hook wrapping Vercel AI SDK's useChat()
 *
 * Manages conversation state, message streaming, analytics events,
 * and localStorage persistence for surviving minimize/close/refresh.
 *
 * Compatible with AI SDK v6 (useChat from @ai-sdk/react).
 */

"use client";

import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import type { UIMessage } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { trackEvent, getPostHogContext } from "@/lib/analytics/posthog";
import { CONVERSION_LABELS, trackGoogleAdsConversion } from "@/lib/google-ads/gtag";
import { estimateCommissionEur } from "@/lib/provision/estimate";
import { MAX_PRICE_ARG, QUANTITY_ARG } from "@/lib/chat/tools/biotopausgleich-args";
import type { QuickFlowContext } from "@/lib/chat/types";
import { readFormDataFromStorage, extractFormSummary } from "@/hooks/useQuickFlowContext";
import { CHAT_STORAGE_KEY, CHAT_STATE_MAX_AGE_MS } from "@/lib/chat/client-state";
import { isAuthTransitionPending } from "@/lib/auth/transition";
import { useAuthTransition } from "@/lib/auth/hooks/useAuthTransition";

/** localStorage key — exported so ChatWidget can check for saved state */
export { CHAT_STORAGE_KEY } from "@/lib/chat/client-state";

/** Max age for saved conversations (24 hours) */
const MAX_AGE_MS = CHAT_STATE_MAX_AGE_MS;
interface PersistedChatState {
  messages: UIMessage[];
  conversationId: string | null;
  savedAt: number;
  draftInput?: string;
  /**
   * Entity whose email confirmation the user has already completed.
   *
   * The tool result frozen in `messages` says `email_verification_pending: true`
   * forever — it was true when the tool ran. Without this the flow footer would
   * re-open a finished step after every reload and ask the user to confirm an
   * address they already confirmed. Same staleness the prompt side solves with
   * `conversationEntity` (see app/api/chat/route.ts).
   */
  verifiedEntityId?: string | null;
}
interface UseSalesChatOptions {
  /** Quick-flow form context (step, form data) — passed by ChatPanel */
  quickFlowContext?: QuickFlowContext | null;
  /**
   * Race fallback: Cloudflare WAF returns `cf-mitigated: challenge` for
   * /api/chat when the off-screen widget hasn't minted `cf_clearance` yet.
   * Resolving the visible challenge sets the cookie; we then retry once.
   */
  requestInteractiveToken?: () => Promise<string>;
  /** PostHog distinct_id from the browser (for server-side event attribution) */
  posthogDistinctId?: string;
}

/**
 * Read persisted chat state from localStorage.
 * Returns null if no saved state, state is expired, or parse fails.
 */
function restoreState(): PersistedChatState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: PersistedChatState = JSON.parse(raw);
    // Expire after 24 hours
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(CHAT_STORAGE_KEY);
      return null;
    }
    if (!parsed.messages || parsed.messages.length === 0) return null;
    return parsed;
  } catch {
    localStorage.removeItem(CHAT_STORAGE_KEY);
    return null;
  }
}
export function useSalesChat(options?: UseSalesChatOptions) {
  // Restore persisted state on first render (lazy initialiser — runs once)
  const restored = useMemo(() => restoreState(), []);
  const [conversationId, setConversationId] = useState<string | null>(() => restored?.conversationId ?? null);

  // Survives reload; see PersistedChatState.verifiedEntityId.
  const [verifiedEntityId, setVerifiedEntityId] = useState<string | null>(() => restored?.verifiedEntityId ?? null);

  // WHY a ref: conversationId is set from the X-Conversation-Id response header
  // inside the memoized fetch closure — before React re-renders and useMemo
  // rebuilds the transport. The ref bridges that timing gap.
  const conversationIdRef = useRef<string | null>(conversationId);
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);
  const [input, setInput] = useState(restored?.draftInput ?? "");
  const lastSubmittedRef = useRef("");
  const authTransition = useAuthTransition();

  // WHY a ref: message count is only used for analytics event payloads,
  // never drives rendering. A ref avoids unnecessary re-renders.
  const messageCountRef = useRef(0);
  const pathname = usePathname();

  // WHY a single ref: the transport's fetch closure is memoized, so it captures
  // stale prop values. Instead of syncing 4 separate refs for each option prop,
  // we bundle them into one "latest options" ref that the closure reads at
  // send-time. This keeps the hook body clean while guaranteeing freshness.
  const latestOptionsRef = useRef<UseSalesChatOptions>(options ?? {});
  useEffect(() => {
    latestOptionsRef.current = options ?? {};
  });

  // Build transport with custom fetch to capture X-Conversation-Id header.
  // The transport is memoized so useChat doesn't reset on every render.
  // Fresh per-request values (token, context, conversationId) are read from
  // refs at send-time inside the fetch closure — see latestOptionsRef above.
  const transport = useMemo(() =>
  // eslint-disable-next-line react-hooks/refs -- refs are only read inside fetch callback (async), not during memo construction
  new DefaultChatTransport({
    api: "/api/chat",
    body: {
      conversationId,
      posthogDistinctId: options?.posthogDistinctId,
      currentUrl: pathname
    },
    fetch: async (url, init) => {
      const opts = latestOptionsRef.current;

      // Inject send-time data into the request body (single parse/stringify pass).
      // The transport body is memoized, so we patch in values that must be fresh.
      if (init && typeof init.body === "string") {
        try {
          const parsed = JSON.parse(init.body);

          // Fresh conversation ID (ref is updated immediately when
          // X-Conversation-Id header is received — before useMemo rebuilds)
          parsed.conversationId = conversationIdRef.current;

          // Fresh acquisition context (session id + attribution) so the
          // server-side chatbot_conversion event is attributable to its
          // paid/organic source and links to its session recording.
          parsed.posthogContext = getPostHogContext();

          // Fresh form data from localStorage (may change within a step)
          const ctx = opts.quickFlowContext;
          if (ctx) {
            const freshRawData = readFormDataFromStorage(ctx.flow);
            parsed.quickFlowContext = {
              ...ctx,
              formSummary: freshRawData ? extractFormSummary(ctx.flow, freshRawData) : null
            };
          }
          init = {
            ...init,
            body: JSON.stringify(parsed)
          };
        } catch {
          // non-critical — send without modifications
        }
      }
      let response = await globalThis.fetch(url, init);

      // Race fallback: if the user opens the chat before the off-screen
      // Turnstile widget has minted the `cf_clearance` cookie, Cloudflare's
      // WAF Managed Challenge intercepts the request and responds with
      // `cf-mitigated: challenge` (plus an HTML challenge page). Show the
      // interactive Turnstile, then retry — the cookie travels with the
      // retry automatically.
      const cfMitigated = response.headers.get("cf-mitigated");
      if (cfMitigated === "challenge" && opts.requestInteractiveToken && !response.ok) {
        try {
          await opts.requestInteractiveToken();
          response = await globalThis.fetch(url, init);
        } catch {
          // Interactive challenge cancelled or failed — return original CF response
        }
      }

      // Capture conversation ID from the API response
      const convId = response.headers.get("X-Conversation-Id");
      if (convId && !conversationIdRef.current) {
        conversationIdRef.current = convId;
        setConversationId(convId);
      }
      return response;
    }
  }), [conversationId, pathname, options?.posthogDistinctId]);
  const chat = useChat({
    id: "sales-chat",
    messages: restored?.messages,
    transport,
    onError: (error: Error) => {
      console.error("[useSalesChat] Stream error:", error);

      // An auth handoff may be recoverable. Keep the original ID/transcript;
      // silently clearing it creates an unrelated, empty conversation.
      setInput(current => current || lastSubmittedRef.current);
    }
  });
  const isLoading = authTransition || chat.status === "submitted" || chat.status === "streaming";

  // ─── localStorage persistence ─────────────────────────────────────
  // Preserve both completed turns and the user's retry draft after an error.
  // Login recovery navigates/reloads, so error state must survive that too.
  useEffect(() => {
    if (chat.status !== "ready" && chat.status !== "error" || chat.messages.length === 0) return;
    try {
      const state: PersistedChatState = {
        messages: chat.messages,
        conversationId: conversationIdRef.current,
        savedAt: Date.now(),
        verifiedEntityId,
        draftInput: input
      };
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // localStorage might be full or disabled — non-critical
    }
  }, [chat.status, chat.messages, verifiedEntityId, input]);

  // ─── Google Ads: Klara conversion micro-conversion ────────────────
  const firedConversionsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (chat.status !== "ready" || !CONVERSION_LABELS.klaraConversion) return;
    const CONVERSION_TOOLS = ["create_offering_draft", "create_request_draft"];
    for (const msg of chat.messages) {
      if (msg.role !== "assistant") continue;
      for (const part of msg.parts ?? []) {
        // AI SDK v6: tool parts have type "tool-{toolName}", state on the part directly
        const toolName = part.type.startsWith("tool-") ? part.type.slice(5) : null;
        // Only a call that created the entry counts. A create tool also
        // FINISHES when it asks a question instead (needs_naturraum_selection,
        // needs_kompensationsraum_selection) or refuses (success: false), and
        // the conversion for the same lead fires with the later call that does
        // create it.
        if (toolName && CONVERSION_TOOLS.includes(toolName) && "state" in part && part.state === "output-available" && "output" in part && (part.output as {
          success?: unknown;
        } | null)?.success === true && "toolCallId" in part && typeof part.toolCallId === "string" && !firedConversionsRef.current.has(part.toolCallId)) {
          firedConversionsRef.current.add(part.toolCallId);
          // Conversion value mirrors the Mindestprovision rule:
          // max(qty × price × COMMISSION_RATE, MIN_PROVISION_EUR_NET).
          let value: number | undefined;
          if ("input" in part && typeof part.input === "object" && part.input) {
            const input_0 = part.input as Record<string, unknown>;
            if (toolName === "create_offering_draft") {
              const qty = Number(input_0.quantity) || 0;
              const price = Number(input_0.price) || 0;
              value = estimateCommissionEur(qty, price) ?? undefined;
            } else {
              // The Biotopausgleich's amount and ceiling, in the same unit (Punkte or Hektar).
              const qty_0 = Number(input_0[QUANTITY_ARG]) || 0;
              const price_0 = Number(input_0[MAX_PRICE_ARG]) || 0;
              value = estimateCommissionEur(qty_0, price_0) ?? undefined;
            }
          }
          trackGoogleAdsConversion(CONVERSION_LABELS.klaraConversion, value);
        }
      }
    }
  }, [chat.status, chat.messages]);

  // ─── Send message + analytics ─────────────────────────────────────
  const sendMessage = useCallback(async (text?: string) => {
    const messageText = text ?? input;
    if (!messageText.trim() || isAuthTransitionPending()) return;
    messageCountRef.current += 1;
    trackEvent("chatbot_message_sent", {
      conversation_id: conversationIdRef.current,
      message_length: messageText.length,
      message_number: messageCountRef.current
    });
    setInput("");
    lastSubmittedRef.current = messageText;
    try {
      await chat.sendMessage({
        text: messageText
      });
    } catch {
      setInput(current_0 => current_0 || messageText);
    }
  }, [chat, input]);

  // Handle input change (for controlled textarea)
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  }, []);

  // Handle form submit
  const handleSubmit = useCallback((e_0?: React.FormEvent) => {
    if (e_0) e_0.preventDefault();
    sendMessage();
  }, [sendMessage]);

  // Track quick action clicks
  const trackQuickAction = useCallback((action: string) => {
    trackEvent("chatbot_quick_action_clicked", {
      action
    });
  }, []);

  // Narrow wrapper, not the raw setter: exposing Dispatch<SetStateAction<…>>
  // under this name means a caller handing it a FUNCTION stores an updater
  // instead of an id, and the confirmation step silently never closes.
  const markEntityVerified = useCallback((entityId: string) => {
    setVerifiedEntityId(entityId);
  }, []);

  // ─── Reset chat ────────────────────────────────────────────────────
  // Clears messages, localStorage, and conversation state to start fresh.
  const resetChat = useCallback(() => {
    trackEvent("chatbot_reset", {
      conversation_id: conversationIdRef.current,
      message_count: messageCountRef.current
    });

    // Clear AI SDK message state
    chat.setMessages([]);

    // Clear persisted state
    localStorage.removeItem(CHAT_STORAGE_KEY);

    // Reset conversation tracking
    conversationIdRef.current = null;
    setConversationId(null);
    messageCountRef.current = 0;
    // A new conversation captures a new entity; carrying the old id over would
    // mark the next flow's confirmation step done before it exists.
    setVerifiedEntityId(null);

    // Clear input field
    setInput("");
  }, [chat]);
  return {
    messages: chat.messages,
    status: chat.status,
    error: chat.error,
    isLoading,
    input,
    setInput,
    handleInputChange,
    handleSubmit,
    sendMessage,
    resetChat,
    conversationId,
    trackQuickAction,
    verifiedEntityId,
    markEntityVerified
  };
}